import { useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Hook to listen for service worker messages and handle cache invalidation
 * Uses Workbox's BroadcastUpdatePlugin for automatic cache invalidation
 */
export const useServiceWorkerMessages = () => {
    const queryClient = useQueryClient();

    // Function to clear module caches when React context issues occur
    const clearModuleCache = useCallback(() => {
        // Don't clear cache if offline - preserve offline functionality
        if (!navigator.onLine) {
            console.warn('🚫 Offline detected - preserving cached modules for offline functionality');
            return;
        }

        if (navigator.serviceWorker?.controller) {
            navigator.serviceWorker.controller.postMessage({
                type: 'CLEAR_MODULE_CACHE'
            });
        }
    }, []);

    // Function to skip waiting for service worker updates
    const skipWaiting = useCallback(() => {
        if (navigator.serviceWorker?.controller) {
            navigator.serviceWorker.controller.postMessage({
                type: 'SKIP_WAITING'
            });
        }
    }, []);

    useEffect(() => {
        const handleServiceWorkerMessage = (event: MessageEvent) => {
            const { type, data } = event.data || {};

            // Add debugging for malformed messages
            if (!type) {
                console.warn('⚠️ Service worker message received without type:', event.data);
                return;
            }

            // Add debugging for CACHE_UPDATED messages to help identify the source (dev only)
            if (type === 'CACHE_UPDATED' && process.env.NODE_ENV === 'development') {
                console.log('🔍 CACHE_UPDATED message received:', {
                    type,
                    data,
                    hasData: 'data' in event.data,
                    dataType: typeof data,
                    meta: event.data?.meta,
                    payload: event.data?.payload,
                    fullEventData: event.data
                });
            }

            switch (type) {
                case 'SW_LOG':
                    // Optional: Forward service worker logs to main console
                    if (data && typeof data === 'object' && data.message) {
                        console.log(data.message, ...(data.args || []));
                    } else {
                        console.warn('⚠️ SW_LOG message received with invalid data structure:', data);
                    }
                    break;

                case 'SW_READY':
                    if (process.env.NODE_ENV === 'development') {
                        console.log('✅ Service Worker ready:', data);
                    }
                    break;

                case 'MODULE_CACHE_CLEARED':
                    // Handle module cache cleared notification
                    if (process.env.NODE_ENV === 'development') {
                        console.log('🧹 Module cache cleared by service worker:', data);
                    }
                    if (data && typeof data === 'object' && data.surgical) {
                        if (process.env.NODE_ENV === 'development') {
                            console.log('✅ Surgical cache clearing completed - offline functionality preserved');
                        }
                        // Don't reload immediately for surgical clearing, let React handle it gracefully
                        // Only reload if we detect actual React context errors
                    } else {
                        // Force a page reload to get fresh modules (for wholesale clearing)
                        setTimeout(() => {
                            window.location.reload();
                        }, 100);
                    }
                    break;

                default:
                    // Ignore unknown message types
                    break;
            }
        };

        // Listen for messages from service worker
        navigator.serviceWorker?.addEventListener('message', handleServiceWorkerMessage);

        return () => {
            navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage);
        };
    }, [queryClient]);

    return {
        clearModuleCache,
        skipWaiting
    };
};
